import React, { useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Form, FormInstance, Schema, SelectPicker,
} from 'rsuite';
import _ from 'lodash';
import axios from 'axios';
import {
  AllegroCreateResponsiblePersonRequest,
  AllegroResponsiblePerson,
  AllegroUpdateResponsiblePersonRequest,
} from '../../../../api/allegro/responsibleperson/responsiblePersonTypes';
import { createAllegroResponsiblePerson, updateAllegroResponsiblePerson } from '../../../../api/allegro/responsibleperson/responsiblePerson';
import { showErrorMessage, showSuccessMessage } from '../../../../utils/message';

const COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
  'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
].map((countryCode) => ({
  value: countryCode,
  label: countryCode,
}));

const validationModel = Schema.Model({
  label: Schema.Types.StringType()
    .isRequired('Label is required.')
    .maxLength(50, 'Max 50 characters.'),
  name: Schema.Types.StringType()
    .isRequired('Name is required.')
    .maxLength(200, 'Max 200 characters.'),
  street: Schema.Types.StringType()
    .isRequired('Street is required.')
    .maxLength(200, 'Max 200 characters.'),
  postalCode: Schema.Types.StringType()
    .isRequired('Postal code is required.')
    .maxLength(20, 'Max 20 characters.'),
  city: Schema.Types.StringType()
    .isRequired('City is required.')
    .maxLength(100, 'Max 100 characters.'),
  countryCode: Schema.Types.StringType().isRequired('Country code is required.'),
  email: Schema.Types.StringType()
    .maxLength(50, 'Max 50 characters.')
    .isEmail('Email is not valid.')
    .addRule(
      (value, edit: Edit) => !(_.isEmpty(value) && _.isEmpty(edit.formUrl)),
      'Email or form URL is required.',
      true,
    ),
  phoneNumber: Schema.Types.StringType().maxLength(30, 'Max 30 characters.'),
  formUrl: Schema.Types.StringType()
    .maxLength(80, 'Max 80 characters.')
    .addRule(
      (value, edit: Edit) => !(_.isEmpty(value) && _.isEmpty(edit.email)),
      'Email or form URL is required.',
      true,
    ).addRule(
      (value) => _.isEmpty(value) || value.startsWith('https://'),
      'Form URL must start with "https://".',
    ),
});

interface Props {
  marketplaceCode: string;
  responsiblePerson?: AllegroResponsiblePerson;
  onCancel: () => void;
  onSaved: () => void;
}

type Status = 'ready' | 'saving';

interface Edit {
  label: string;
  name: string;
  street: string;
  postalCode: string;
  city: string;
  countryCode: string | null;
  email: string;
  phoneNumber: string;
  formUrl: string;
}

interface State {
  status: Status;
  edit: Edit;
}

const initialEdit: Edit = {
  label: '',
  name: '',
  street: '',
  postalCode: '',
  city: '',
  countryCode: null,
  email: '',
  phoneNumber: '',
  formUrl: '',
};

function getInitialState(responsiblePerson?: AllegroResponsiblePerson): State {
  if (!responsiblePerson) {
    return {
      status: 'ready',
      edit: initialEdit,
    };
  }

  return {
    status: 'ready',
    edit: {
      label: responsiblePerson.name,
      name: responsiblePerson.personalData.name,
      street: responsiblePerson.personalData.address.street,
      postalCode: responsiblePerson.personalData.address.postalCode,
      city: responsiblePerson.personalData.address.city,
      countryCode: responsiblePerson.personalData.address.countryCode,
      email: responsiblePerson.personalData.contact.email ?? '',
      phoneNumber: responsiblePerson.personalData.contact.phoneNumber ?? '',
      formUrl: responsiblePerson.personalData.contact.formUrl ?? '',
    },
  };
}

type EditSetAction = { type: 'edit/set', payload: Edit };
type StatusSetAction = { type: 'status/set', payload: Status };
type Action = EditSetAction | StatusSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'edit/set': {
      return { ...state, edit: action.payload };
    }
    case 'status/set': {
      return { ...state, status: action.payload };
    }
    default:
      return state;
  }
}

function mapCreateRequest(
  edit: Edit,
): AllegroCreateResponsiblePersonRequest {
  return {
    name: edit.label,
    personalData: {
      name: edit.name,
      address: {
        countryCode: edit.countryCode ?? '',
        street: edit.street,
        postalCode: edit.postalCode,
        city: edit.city,
      },
      contact: {
        email: edit.email || undefined,
        phoneNumber: edit.phoneNumber || undefined,
        formUrl: edit.formUrl || undefined,
      },
    },
  };
}

function mapUpdateRequest(
  responsiblePerson: AllegroResponsiblePerson,
  edit: Edit,
): AllegroUpdateResponsiblePersonRequest {
  return {
    id: responsiblePerson.id,
    ...mapCreateRequest(edit),
  };
}

export default function EshopSetupAllegroResponsiblePersonEdit({
  marketplaceCode,
  responsiblePerson,
  onCancel,
  onSaved,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, responsiblePerson, getInitialState);
  const formRef = useRef<FormInstance>(null);

  async function saveResponsiblePerson() {
    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      const { edit } = state;

      if (responsiblePerson) {
        await updateAllegroResponsiblePerson(
          marketplaceCode,
          responsiblePerson.id,
          mapUpdateRequest(responsiblePerson, edit),
        );
      } else {
        await createAllegroResponsiblePerson(
          marketplaceCode,
          mapCreateRequest(edit),
        );
      }

      showSuccessMessage('Responsible person successfully saved.');
      onSaved();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showErrorMessage(JSON.stringify(error.response?.data));
      }
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleSave() {
    if (!formRef.current?.check()) {
      return;
    }

    saveResponsiblePerson().then();
  }

  return (
    <Form
      ref={formRef}
      fluid
      formValue={state.edit}
      onChange={(edit) => dispatch({ type: 'edit/set', payload: edit as Edit })}
      model={validationModel}
      layout="vertical"
    >
      <Form.Group>
        <Form.ControlLabel>Label</Form.ControlLabel>
        <Form.Control name="label" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Name</Form.ControlLabel>
        <Form.Control name="name" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Street</Form.ControlLabel>
        <Form.Control name="street" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Postal code</Form.ControlLabel>
        <Form.Control name="postalCode" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>City</Form.ControlLabel>
        <Form.Control name="city" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Country code</Form.ControlLabel>
        <Form.Control
          name="countryCode"
          accepter={SelectPicker}
          data={COUNTRY_CODES}
          cleanable={false}
          searchable
          block
        />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Email</Form.ControlLabel>
        <Form.Control name="email" type="email" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Phone number</Form.ControlLabel>
        <Form.Control name="phoneNumber" type="tel" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Form URL</Form.ControlLabel>
        <Form.Control name="formUrl" type="text" />
      </Form.Group>
      <ButtonToolbar>
        <Button
          appearance="primary"
          onClick={() => handleSave()}
          loading={state.status === 'saving'}
          type="submit"
        >
          Save
        </Button>
        <Button appearance="ghost" onClick={() => onCancel()}>
          Cancel
        </Button>
      </ButtonToolbar>
    </Form>
  );
}

EshopSetupAllegroResponsiblePersonEdit.defaultProps = {
  responsiblePerson: undefined,
};
